import React, { useContext, useEffect } from 'react';

import { partnerOrderTableSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { StageBreadCrumb } from 'components/BreadCrumbNavigation/BreadCrumbNavigation';
import { OrderDetails } from 'pages/partners/components/OrderDetails/OrderDetails';
import { OrderGMetrixTable } from 'components/OrderGMetrixTable/OrderGMetrixTable';
import { PlaceAnOrder } from 'components/PlaceAnOrder/PlaceAnOrder';
import { RenewAnOrder } from 'components/RenewAnOrder/RenewAnOrder';
import { PartnerDetails, PartnerTypes } from 'apis/PartnerApi';
import { useRegisterHeaderControls } from 'components/layout';
import { Order, OrderApi, OrderStatus } from 'apis/OrderApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useNotification } from 'app/notifications';
import { filterDataBySearchTerm } from 'utils';
import { License } from 'apis/LicenseApi';

import 'pages/partners/components/OrdersTab/styles/orders-tab.scss';

import { useSuperState } from 'hooks';

interface OrdersTabProps {
    details: PartnerDetails;
    licenses: License[];
}

export interface AutoFillOption {
    disabled: boolean;
    label: string;
    value: number;
}

export type OrderStageOption = 'Lookup' | 'Place' | 'Retract' | 'Renew' | 'Details';

enum ProcRec {
    Placed = 1,
    Recieved
}

export const OrdersTab = ({ details, licenses }: OrdersTabProps) => {

    const { formatMessage } = useIntl();
    const { permissions } = usePartners();
    const { catchWithToast, createToast } = useNotification();
    const [state, setState] = useSuperState({
        breadCrumbs: [{ text: formatMessage({ id: 'orders.2fb22e52', defaultMessage: 'Orders' }), to: 'Lookup' }] as StageBreadCrumb<OrderStageOption>[],
        filteredOrders: [] as Order[],
        loadingGridData: true,
        orderAutoFillOptions: [
            { disabled: false, label: formatMessage({ id: 'do_not_fill.eeb8112f', defaultMessage: 'Do Not Fill' }), value: 0 },
            { disabled: true, label: formatMessage({ id: 'from_inventory.55842df5', defaultMessage: 'From Inventory' }), value: 1 },
            { disabled: true, label: formatMessage({ id: 'with_a_new_order.ea069c7e', defaultMessage: 'With a New Order' }), value: 2 }
        ] as AutoFillOption[],
        orderDetails: undefined as Order | undefined,
        orderFilterValue: { id: OrderStatus.Processed, text: formatMessage({ id: 'processed_orders.44341883', defaultMessage: 'Processed Orders' }) },
        orders: [] as Order[],
        orderStatuses: [OrderStatus.Pending, OrderStatus.Processed, OrderStatus.Canceled] as OrderStatus[],
        processedRecievedValue: details.PartnerType === PartnerTypes.TestingCenter ? { id: ProcRec.Placed, text: formatMessage({ id: 'placed_orders.cd0237b2', defaultMessage: 'Placed Orders' }) } : { id: ProcRec.Recieved, text: formatMessage({ id: 'received_orders.e7308840', defaultMessage: 'Received Orders' }) || 'Received Orders' },
        searchTerm: '',
        selectedOrder: undefined as Order | undefined,
        stage: 'Lookup' as OrderStageOption
    });
    const { breadCrumbs, filteredOrders, loadingGridData, orderAutoFillOptions, orderDetails, orderFilterValue, orders, orderStatuses, processedRecievedValue, searchTerm, selectedOrder, stage } = state;

    const getRenewalOrderDetailsClick = (orderId: number) => {

        const dataItem = orders.find(order => order.ID === orderId);
        if (dataItem) {

            setState({ breadCrumbs: [{ text: formatMessage({ id: 'orders.2fb22e52', defaultMessage: 'Orders' }), to: 'Lookup' }, { text: formatMessage({ id: 'order_details.f1808d77', defaultMessage: 'Order Details' }), to: 'Details' }], orderDetails: dataItem, stage: 'Details' });

        }

    };

    const renewOrRetractOrderDetails = async (orderId: number, renewOrRetract: 'Renew' | 'Retract') => {

        const dataItem = orders.find(order => order.ID === orderId);
        if (dataItem) {

            if (renewOrRetract === 'Renew') {

                setState({ breadCrumbs: [...breadCrumbs, { text: formatMessage({ id: 'renew_order.e768c154', defaultMessage: 'Renew Order' }), to: 'Renew' }], orderFilterValue: { id: OrderStatus.AllOrders, text: formatMessage({ id: 'all_orders.55e68bdc', defaultMessage: 'All Orders' }) }, selectedOrder: dataItem, stage: 'Renew' });

            } else {

                if (dataItem.OrderStatus !== OrderStatus.Canceled) {

                    try {

                        const response = await OrderApi.RetractOrder(dataItem.Id);
                        if (response.Done) {

                            setState({ loadingGridData: true });
                            getAllOrders(orderFilterValue.id);
                            createToast(formatMessage({ id: 'succesfully_retracted_the_order.e09e0d4c', defaultMessage: 'Succesfully Retracted the Order.' }), 'success');

                            resetBackToLookup();

                        } else {

                            createToast(response.Message, 'danger');

                        }

                    } catch (e) {

                        catchWithToast(formatMessage({ id: 'failed_to_retract_order.6c20a95d', defaultMessage: 'Failed to retract order.' }))(e);

                    }

                }

            }

        }

    };

    const resetBackToLookup = () => {

        setState({ breadCrumbs: [{ text: formatMessage({ id: 'orders.2fb22e52', defaultMessage: 'Orders' }), to: 'Lookup' }], stage: 'Lookup' });

    };

    const onDetailsItemClick = (dataItem: Order) => {

        setState({ breadCrumbs: [...breadCrumbs, { text: dataItem.License.Name, to: 'Details' }], orderDetails: dataItem, stage: 'Details' });

    };

    useEffect(() => {

        getAllOrdersFiltered();

    }, [processedRecievedValue]);

    const getAllOrdersFiltered = (placeNewOrder = false) => {

        setState({ loadingGridData: true });
        getAllOrders(0);
        resetBackToLookup();
        if (placeNewOrder) {

            setState({ orderFilterValue: { id: OrderStatus.AllOrders, text: formatMessage({ id: 'all_orders.55e68bdc', defaultMessage: 'All Orders' }) } });

        }

    };

    const getAllOrders = async (filterValue = 0) => {

        try {

            const orders = await OrderApi.GetAll(details.ID, filterValue, processedRecievedValue.id === ProcRec.Placed ? false : true, 1);
            setState({ orders: orders.map(order => {

                order.Id = order.ID;
                return order;

            }) });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_orders.bc72e0a6', defaultMessage: 'Failed to load orders.' }))(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const placeNewOrderClick = async () => {

        setState({ breadCrumbs: [...breadCrumbs, { text: formatMessage({ id: 'place_an_order.00df5f29', defaultMessage: 'Place an Order' }), to: 'Place' }], stage: 'Place' });

    };

    const handleBreadCrumbClick = (currentBreadCrumb: StageBreadCrumb<OrderStageOption>) => {

        const index = breadCrumbs.findIndex(breadCrumb => breadCrumb.to === currentBreadCrumb.to);
        const newbreadCrumbs = [...breadCrumbs];
        newbreadCrumbs.splice(index + 1);
        setState({ breadCrumbs: newbreadCrumbs, stage: currentBreadCrumb.to });

    };

    useEffect(() => {

        const data = filterDataBySearchTerm(searchTerm, orders);
        filterDataByStatus(data);

    }, [searchTerm, orders, orderStatuses]);

    const handleOrderStatusChange = (status: OrderStatus, checked: boolean) => {

        setState({ orderStatuses: checked ? [...orderStatuses, status] : orderStatuses.filter(current => current !== status) });

    };

    const filterDataByStatus = (orders: Order[]) => {

        if (orderStatuses.length === 0) {

            setState({ filteredOrders: orders });

        } else {

            setState({ filteredOrders: orders.filter(order => orderStatuses.includes(order.OrderStatus)) });

        }

    };

    const updateOrderDetailsTemp = (order: Order) => {

        const updatedOrders = [...orders];
        const orderToUpdate = updatedOrders.findIndex(orderToUpdate => orderToUpdate.ID === order.ID);
        if (orderToUpdate !== -1) {

            updatedOrders[orderToUpdate] = order;

        }
        setState({ orders: updatedOrders });

    };

    // Dial the "Place an Order" CTA out to the partner detail page header, but
    // only while the lookup table (not a place/renew/details flow) is showing.
    useRegisterHeaderControls(stage === 'Lookup' ? {
        cta: {
            disabled: !permissions.PlaceAnOrder || !permissions.Edit || loadingGridData,
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>
                        <FormattedMessage defaultMessage="Place an Order" id="place_an_order.00df5f29" />
                    </span>
                </div>
            ),
            onClick: () => placeNewOrderClick()
        }
    } : null);

    return (
        <>
            {stage === 'Lookup' ? (
                <OrderGMetrixTable
                    columnData={[
                        { field: 'OrderControl', title: formatMessage({ id: 'order_id.ab576b3d', defaultMessage: 'Order ID', description: 'Formulario' }) },
                        { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                        { field: 'OrderStatusText', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) },
                        { field: 'DateExpire', title: formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' }) },
                        { columnsDropdownSelected: false, field: 'DateStart', title: formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' }) },
                        { columnsDropdownSelected: false, field: 'DateCreate', title: formatMessage({ id: 'place_date.bbf1373c', defaultMessage: 'Place Date' }) }
                    ]}
                    advancedSearchFields={partnerOrderTableSearchFields}
                    allowExport={true}
                    loadingGridData={loadingGridData}
                    onDetailsItemClick={onDetailsItemClick}
                    onOrderStatusChange={handleOrderStatusChange}
                    orderFilterValue={orderFilterValue}
                    orders={filteredOrders}
                    orderStatuses={orderStatuses}
                    partnerType={details.PartnerType}
                    processedRecievedValue={processedRecievedValue}
                    searchTerm={searchTerm}
                    selectableRows={false}
                    setOrderFilterValue={val => setState({ orderFilterValue: val })}
                    setProcessedRecievedValue={val => setState({ processedRecievedValue: val })}
                    setSearchTerm={val => setState({ searchTerm: val })}
                />
            ) : stage === 'Place' ? (
                <PlaceAnOrder
                    breadCrumbs={breadCrumbs}
                    details={details}
                    getAllOrdersFiltered={getAllOrdersFiltered}
                    handleBreadCrumbClick={handleBreadCrumbClick}
                    orderableLicenses={licenses}
                    orderAutoFillOptions={orderAutoFillOptions}
                    setOrderAutoFillOptions={val => setState({ orderAutoFillOptions: val })}
                />
            ) : stage === 'Renew' ? (
                <>
                    {selectedOrder && (
                        <RenewAnOrder
                            breadCrumbs={breadCrumbs}
                            details={details}
                            getAllOrdersFiltered={getAllOrdersFiltered}
                            handleBreadCrumbClick={handleBreadCrumbClick}
                            orderAutoFillOptions={orderAutoFillOptions}
                            selectedOrder={selectedOrder}
                            setOrderAutoFillOptions={val => setState({ orderAutoFillOptions: val })}
                        />
                    )}
                </>
            ) : (
                stage === 'Details' &&
                orderDetails && (
                    <OrderDetails
                        breadCrumbs={breadCrumbs}
                        getRenewalOrderDetailsClick={getRenewalOrderDetailsClick}
                        handleBreadCrumbClick={handleBreadCrumbClick}
                        orderDetails={orderDetails}
                        renewedOrder={orderDetails.RenewId === -1 ? false : true}
                        renewOrRetractOrderDetails={renewOrRetractOrderDetails}
                        resetBackToLookup={resetBackToLookup}
                        updateOrderDetailsTemp={updateOrderDetailsTemp}
                    />
                )
            )}
        </>
    );

};
